import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { fetchInfluencers, formatNumber } from '../../../services/campaignService';

const InfluencerSelection = ({ data, updateData, errors }) => {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    minFollowers: '',
    maxFollowers: '',
    minEngagement: '',
    contentSpecialization: [],
    availabilityStatus: 'available',
    verified: undefined
  });

  const contentSpecializationOptions = [
    { value: 'music', label: 'Music' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'tech', label: 'Tech' },
    { value: 'fashion', label: 'Fashion' },
    { value: 'gaming', label: 'Gaming' },
    { value: 'food', label: 'Food' },
    { value: 'travel', label: 'Travel' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'beauty', label: 'Beauty' },
    { value: 'education', label: 'Education' }
  ];

  useEffect(() => {
    loadInfluencers();
  }, []);

  const loadInfluencers = async () => {
    try {
      setLoading(true);
      const result = await fetchInfluencers(filters);
      setInfluencers(result);
    } catch (error) {
      console.error('Error loading influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    updateData('minFollowers', filters?.minFollowers);
    updateData('maxFollowers', filters?.maxFollowers);
    updateData('minEngagement', filters?.minEngagement);
    updateData('contentSpecialization', filters?.contentSpecialization);
    loadInfluencers();
  };

  const toggleInfluencer = (influencerId) => {
    const currentSelected = data?.selectedInfluencers || [];
    if (currentSelected?.includes(influencerId)) {
      updateData('selectedInfluencers', currentSelected?.filter(id => id !== influencerId));
    } else {
      updateData('selectedInfluencers', [...currentSelected, influencerId]);
    }
  };

  const isSelected = (influencerId) => {
    return data?.selectedInfluencers?.includes(influencerId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Influencer Selection</h2>
        <p className="text-muted-foreground">Find and select influencers for your campaign</p>
      </div>

      {/* Filters */}
      <div className="bg-muted rounded-lg p-4 space-y-4">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Icon name="Filter" size={18} />
          Filter Influencers
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Min Followers"
            type="number"
            placeholder="10000"
            value={filters?.minFollowers || ''}
            onChange={(e) => setFilters({ ...filters, minFollowers: e?.target?.value })}
          />
          <Input
            label="Max Followers"
            type="number"
            placeholder="500000"
            value={filters?.maxFollowers || ''}
            onChange={(e) => setFilters({ ...filters, maxFollowers: e?.target?.value })}
          />
          <Input
            label="Min Engagement Rate (%)"
            type="number"
            placeholder="3.5"
            value={filters?.minEngagement || ''}
            onChange={(e) => setFilters({ ...filters, minEngagement: e?.target?.value })}
          />
        </div>
        <Select
          label="Content Specialization"
          placeholder="Select specializations"
          options={contentSpecializationOptions}
          value={filters?.contentSpecialization || []}
          onChange={(value) => setFilters({ ...filters, contentSpecialization: value })}
          multiple
          searchable
        />
        <Button
          onClick={handleApplyFilters}
          iconName="Search"
          iconPosition="left"
          fullWidth
        >
          Apply Filters
        </Button>
      </div>

      {/* Selected Count */}
      {data?.selectedInfluencers?.length > 0 && (
        <div className="bg-primary/10 border border-primary rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="Users" size={20} className="text-primary" />
            <span className="font-medium text-foreground">
              {data?.selectedInfluencers?.length} influencer{data?.selectedInfluencers?.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => updateData('selectedInfluencers', [])}
          >
            Clear All
          </Button>
        </div>
      )}

      {errors?.selectedInfluencers && (
        <p className="text-sm text-destructive">{errors?.selectedInfluencers}</p>
      )}

      {/* Influencer List */}
      <div>
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading influencers...</p>
          </div>
        ) : influencers?.length === 0 ? (
          <div className="text-center py-12 bg-muted rounded-lg">
            <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-foreground font-medium mb-2">No influencers found</p>
            <p className="text-muted-foreground text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {influencers?.map((influencer) => (
              <div
                key={influencer?.id}
                className={`border-2 rounded-lg p-4 transition-all cursor-pointer ${
                  isSelected(influencer?.id)
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
                }`}
                onClick={() => toggleInfluencer(influencer?.id)}
              >
                <div className="flex items-start gap-4">
                  <img
                    src={influencer?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_15f94130f-1763296604240.png'}
                    alt={`${influencer?.name} profile picture`}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground">{influencer?.name}</h4>
                      {influencer?.verified && (
                        <Icon name="BadgeCheck" size={16} className="text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {influencer?.bio || 'No bio available'}
                    </p>
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1">
                        <Icon name="Users" size={14} className="text-muted-foreground" />
                        <span className="text-foreground font-medium">{formatNumber(influencer?.follower_count)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="TrendingUp" size={14} className="text-muted-foreground" />
                        <span className="text-foreground font-medium">{influencer?.engagement_rate}%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="Tag" size={14} className="text-muted-foreground" />
                        <span className="text-foreground font-medium capitalize">{influencer?.content_specialization}</span>
                      </div>
                    </div>
                  </div>
                  {isSelected(influencer?.id) && (
                    <Icon name="CheckCircle" size={24} className="text-primary flex-shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InfluencerSelection;
